/**
 * RealtimeContext — single WebSocket connection per browser tab.
 *
 * Connects to /api/ws using the current JWT, auto-reconnects with exponential
 * backoff, and exposes:
 *   - subscribe(event, handler)    — local pub/sub
 *   - send({type, channel})         — forwards subscribe/unsubscribe requests
 *
 * The frontend NEVER trusts WS payloads for state; events are HINTS to refetch
 * data via REST. This keeps backend as the source of truth.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

const RealtimeContext = createContext(null);

const WS_URL = (() => {
  const http = process.env.REACT_APP_BACKEND_URL || '';
  const ws = http.replace(/^http/, 'ws');
  return `${ws}/api/ws`;
})();

export const RealtimeProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const handlersRef = useRef(new Map()); // kind -> Set<fn>
  const reconnectRef = useRef({ attempts: 0, timer: null });
  const [status, setStatus] = useState('idle'); // idle | connecting | open | closed

  const subscribe = useCallback((kind, handler) => {
    if (!handlersRef.current.has(kind)) handlersRef.current.set(kind, new Set());
    handlersRef.current.get(kind).add(handler);
    return () => handlersRef.current.get(kind)?.delete(handler);
  }, []);

  const sendChannel = useCallback((type, channel) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type, channel }));
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      if (wsRef.current) wsRef.current.close();
      return;
    }
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      setStatus('connecting');
      const url = `${WS_URL}?token=${encodeURIComponent(token)}`;
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) { ws.close(); return; }
        setStatus('open');
        reconnectRef.current.attempts = 0;
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          const kind = msg.kind;
          if (!kind) return;
          const handlers = handlersRef.current.get(kind);
          if (handlers) handlers.forEach((h) => { try { h(msg.data, msg); } catch (_) {} });
          const wildcard = handlersRef.current.get('*');
          if (wildcard) wildcard.forEach((h) => { try { h(msg.data, msg); } catch (_) {} });
        } catch (_) { /* ignore malformed */ }
      };

      ws.onerror = () => { /* let onclose handle reconnection */ };
      ws.onclose = () => {
        setStatus('closed');
        if (!cancelled) scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      const attempt = reconnectRef.current.attempts + 1;
      reconnectRef.current.attempts = attempt;
      const delay = Math.min(30000, 1000 * 2 ** Math.min(attempt, 6));
      clearTimeout(reconnectRef.current.timer);
      reconnectRef.current.timer = setTimeout(connect, delay);
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectRef.current.timer);
      const ws = wsRef.current;
      if (ws) ws.close();
    };
  }, [token, isAuthenticated]);

  return (
    <RealtimeContext.Provider value={{ status, subscribe, sendChannel }}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeContext) || { status: 'idle', subscribe: () => () => {}, sendChannel: () => {} };
